// src/lib/colors.ts
// Two themes only: light (default) and dark.
// Consumers should read colors via theme.colors (in styled callbacks) or
// useColorTheme() (in functional components) — see lib/theme.ts and
// components/ColorModeProvider.tsx. Do NOT import a theme directly into a
// component; that bypasses the runtime mode toggle.

export interface ColorTheme {
  primary:    { main: string; dark: string; light: string; contrastText: string };
  secondary:  { main: string; dark: string; light: string; contrastText: string };

  background: {
    default:  string;
    paper:    string;
    elevated: string; // cards, modals
    hero:     string; // hero sections (can be a gradient)
  };

  text: {
    primary:   string;
    secondary: string;
    disabled:  string;
  };

  border: {
    primary:   string;
    secondary: string;
    accent:    string; // hover, focus, brand-tinted edges
  };

  brand: {
    accent:  { main: string; dark: string; light: string; contrastText: string };
    neutral: { main: string; dark: string; light: string; contrastText: string };
  };

  gradients: {
    primary:   string;
    secondary: string;
    hero:      string;
    card:      string;
  };

  effects: {
    shadow:   string;
    overlay:  string;
    backdrop: string;
    glow:     string;
  };
}

// ============================================================================
// LIGHT THEME — primary default (Dalhousie brand)
// Off-white page bg so white cards visibly lift; deeper borders/text for
// readable contrast. Hero gradient kept subtle per design call.
// ============================================================================
export const lightTheme: ColorTheme = {
  primary: {
    main: '#FFD400',
    dark: '#E6C000',
    light: '#FFF066',
    contrastText: '#242424',
  },
  secondary: {
    main: '#242424',
    dark: '#1A1A1A',
    light: '#404040',
    contrastText: '#FFFFFF',
  },
  background: {
    default:  '#EDEDEF', // soft warm grey — page canvas
    paper:    '#FFFFFF', // pure white cards lift off the canvas
    elevated: '#E0E0E4', // visibly darker for emphasized panels / filters
    hero:     'linear-gradient(135deg, #E4E4E8 0%, #F0F0F2 100%)',
  },
  text: {
    primary:   '#1A1A1A', // near-black for headings / body
    secondary: '#404040', // dark grey for supporting copy
    disabled:  '#666666', // medium grey, still readable on light bg
  },
  border: {
    primary:   '#C0C0C4', // visible borders against off-white canvas
    secondary: '#D8D8DC', // softer dividers
    accent:    '#FFD400',
  },
  brand: {
    accent:  { main: '#FFD400', dark: '#E6C000', light: '#FFF066', contrastText: '#242424' },
    neutral: { main: '#242424', dark: '#1A1A1A', light: '#404040', contrastText: '#FFFFFF' },
  },
  gradients: {
    primary:   'linear-gradient(45deg, #FFD400, #E6C000)',
    secondary: 'linear-gradient(45deg, #B89000, #242424)', // deeper for contrast on light bg
    hero:      'linear-gradient(135deg, #E4E4E8 0%, #F0F0F2 100%)',
    card:      'linear-gradient(135deg, #FFFFFF 0%, #F5F5F7 100%)',
  },
  effects: {
    shadow:   'rgba(180, 140, 0, 0.25)',
    overlay:  'rgba(36, 36, 36, 0.6)',
    backdrop: 'rgba(0, 0, 0, 0.4)',
    glow:     'rgba(255, 212, 0, 0.20)',
  },
};

// ============================================================================
// DARK THEME — opt-in via toggle (Dalhousie inverted)
// Beak Gold (#FFD400) on Wingtip Black (#1A1A1A) with Cloud White (#FFFFFF) text
// ============================================================================
export const darkTheme: ColorTheme = {
  primary: {
    main: '#FFD400',
    dark: '#E6C000',
    light: '#FFF066',
    contrastText: '#1A1A1A',
  },
  secondary: {
    main: '#404040',
    dark: '#242424',
    light: '#606060',
    contrastText: '#FFFFFF',
  },
  background: {
    default:  '#1A1A1A',
    paper:    '#242424',
    elevated: '#2A2A2A',
    hero:     'linear-gradient(135deg, #242424 0%, #1A1A1A 100%)',
  },
  text: {
    primary:   '#FFFFFF',
    secondary: '#E0E0E0',
    disabled:  '#A0A0A0',
  },
  border: {
    primary:   '#404040',
    secondary: '#303030',
    accent:    '#FFD400',
  },
  brand: {
    accent:  { main: '#FFD400', dark: '#E6C000', light: '#FFF066', contrastText: '#1A1A1A' },
    neutral: { main: '#404040', dark: '#242424', light: '#606060', contrastText: '#FFFFFF' },
  },
  gradients: {
    primary:   'linear-gradient(45deg, #FFD400, #E6C000)',
    secondary: 'linear-gradient(45deg, #FFD400, #FFFFFF)',
    hero:      'linear-gradient(135deg, #242424 0%, #1A1A1A 100%)',
    card:      'linear-gradient(135deg, #242424 0%, #1E1E1E 100%)',
  },
  effects: {
    shadow:   'rgba(255, 212, 0, 0.30)',
    overlay:  'rgba(26, 26, 26, 0.85)',
    backdrop: 'rgba(0, 0, 0, 0.75)',
    glow:     'rgba(255, 212, 0, 0.20)',
  },
};

// ============================================================================
// REGISTRY & MODE
// ============================================================================

export type ColorMode = 'light' | 'dark';

export const themesByMode: Record<ColorMode, ColorTheme> = {
  light: lightTheme,
  dark:  darkTheme,
};

export const DEFAULT_COLOR_MODE: ColorMode = 'light';

// ============================================================================
// UTILITY HELPERS
// ============================================================================

// Convert hex → rgba so we can apply opacity to brand colors at call sites.
// Returns the input unchanged if it's not a hex string (already rgba, hsl, etc).
export const withOpacity = (color: string, opacity: number): string => {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};
